import { supabase } from '../lib/supabase';

export interface CompetitionLeaderboardItem {
  rank: number;
  id: string;
  publicName: string;
  deliveredOrders: number;
}

export interface CompetitionParticipant {
  id: string;
  userId: string;
  mastershopEmail: string;
  publicName: string;
  deliveredOrders: number;
  isHidden: boolean;
  optedInAt: string;
  updatedAt: string;
}

export interface CompetitionLeaderboardData {
  updatedAt: string;
  period: {
    startDate: string;
    finalDate: string;
  };
  totalDeliveredGuides: number;
  totalUniquePartnerSellers: number;
  totalEnrolledCompetitors: number;
  ranking: CompetitionLeaderboardItem[];
}

const SETTINGS_KEY_PARTICIPANTS = 'competition_participants';
const SETTINGS_KEY_LEADERBOARD = 'competition_leaderboard_cache';

export async function competitionRequest(body: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  return fetch('/api/competition/sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

export const competitionService = {
  /**
   * Obtém os dados públicos do ranking da competição
   */
  async getLeaderboard(): Promise<CompetitionLeaderboardData | null> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTINGS_KEY_LEADERBOARD)
        .maybeSingle();

      if (error) {
        console.warn('Erro ao carregar leaderboard da competição:', error);
        return null;
      }

      if (data?.value) {
        return data.value as CompetitionLeaderboardData;
      }
    } catch (err) {
      console.error('Falha ao obter leaderboard:', err);
    }
    return null;
  },

  /**
   * Obtém a lista completa de participantes (apenas para Admin moderar)
   */
  async getAllParticipants(): Promise<CompetitionParticipant[]> {
    try {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTINGS_KEY_PARTICIPANTS)
        .maybeSingle();

      if (data?.value && Array.isArray(data.value)) {
        return data.value as CompetitionParticipant[];
      }
    } catch (err) {
      console.warn('Erro ao obter participantes:', err);
    }
    return [];
  },

  /**
   * Obtém a inscrição do usuário atual, se houver (por userId ou e-mail registrado)
   */
  async getUserParticipation(userId?: string | null): Promise<CompetitionParticipant | null> {
    if (!userId) return null;
    const response = await competitionRequest({ action: 'my-participation' });
    return response.ok ? (await response.json()).participant : null;
  },

  /**
   * Validação de e-mail desativada no cliente (Contenção de Segurança - Fase 0).
   * O fluxo será substituído pela validação oficial com OTP direto da MasterShop.
   */
  async validateMastershopEmail(_email: string): Promise<{
    valid: boolean;
    deliveredCount: number;
    businessName?: string;
    error?: string;
  }> {
    return {
      valid: false,
      deliveredCount: 0,
      error: 'Inscrições pausadas temporariamente para atualização da integração oficial MasterShop.',
    };
  },

  /**
   * Inscreve o vendedor no Desafio Black (Etapa 2 do Modal)
   * Suporta tanto usuários logados quanto visitantes por e-mail MasterShop.
   */
  async registerParticipant(params: {
    userId?: string | null;
    mastershopEmail: string;
    publicName: string;
  }): Promise<{ success: boolean; error?: string; participant?: CompetitionParticipant }> {
    const cleanEmail = params.mastershopEmail.trim().toLowerCase();
    const cleanName = params.publicName.trim();
    const userId = params.userId || null;

    if (!cleanEmail || !cleanEmail.includes('@')) {
      return { success: false, error: 'Por favor, insira um e-mail válido da MasterShop.' };
    }

    if (!cleanName || cleanName.length < 2) {
      return { success: false, error: 'Por favor, defina um nome de exibição de pelo menos 2 caracteres.' };
    }

    // 1. Tenta prioritariamente via backend da Netlify (com privilégio de service_role para contornar RLS)
    try {
      const res = await competitionRequest({
          action: 'register',
          userId: userId,
          mastershopEmail: cleanEmail,
          publicName: cleanName,
        });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          return { success: true, participant: json.participant };
        } else {
          return { success: false, error: json.error || 'Falha ao processar inscrição no servidor.' };
        }
      }
      return { success: false, error: 'Erro ao conectar ao servidor.' };
    } catch (netErr: any) {
      console.warn('Erro ao chamar /api/competition/sync:', netErr.message);
      return { success: false, error: 'Servidor temporariamente indisponível.' };
    }
  },

  /**
   * Recurso de Moderação Administrativa: Alterna a visibilidade de um membro no ranking
   */
  async toggleParticipantVisibility(
    participantId: string,
    isHidden: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Tenta via Netlify backend
      const res = await competitionRequest({
          action: 'toggle-visibility',
          participantId,
          isHidden,
        });

      if (res.ok) {
        const json = await res.json();
        if (json.success) return { success: true };
        return { success: false, error: json.error || 'Erro na moderação.' };
      }
      return { success: false, error: `Servidor retornou HTTP ${res.status}` };
    } catch (e: any) {
      return { success: false, error: e.message || 'Falha ao conectar com o servidor.' };
    }
  },

  /**
   * Moderação Admin: Atualiza o nome público de um participante no ranking
   */
  async updateParticipantName(participantId: string, newPublicName: string): Promise<{ success: boolean; error?: string }> {
    const cleanName = newPublicName.trim();
    if (!cleanName || cleanName.length < 2) {
      return { success: false, error: 'O nome deve conter pelo menos 2 caracteres.' };
    }

    // 1. Tenta via Netlify Functions (service_role)
    try {
      const res = await competitionRequest({
          action: 'update-name',
          participantId,
          newPublicName: cleanName,
        });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          return { success: true };
        } else {
          return { success: false, error: json.error || 'Falha ao atualizar nome no servidor.' };
        }
      }
      return { success: false, error: 'Erro ao conectar ao servidor.' };
    } catch (netErr: any) {
      return { success: false, error: 'Serviço temporariamente indisponível.' };
    }
  },
};

